import { z } from "zod";

import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  ECommonProgramMetaMode,
  EBookableSpaceMetaMode,
} from "@/domains/v4b/program/subprogram.types";

const basePlanningOverrideRangeSchema = z.object({
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
});

const planningOverrideHeaterRangeComfortSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(EHeaterProgramMetaMode.COMFORT),
});
const planningOverrideHeaterRangeEcoSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(EHeaterProgramMetaMode.ECO),
});
const planningOverrideHeaterRangeNoFrostSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(EHeaterProgramMetaMode.NO_FROST),
});

const planningOverrideHeaterRangeTemperatureSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(ECommonProgramMetaMode.TEMP),
  temperature: z.number(),
});

const planningOverrideOffRangeSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(ECommonProgramMetaMode.OFF),
});

const planningOverrideOccupiedRangeSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(EBookableSpaceMetaMode.OCCUPIED),
});
const planningOverrideAvailableRangeSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.literal(EBookableSpaceMetaMode.AVAILABLE),
});

const planningOverrideHeaterRangeSchema = z.discriminatedUnion("metaMode", [
  planningOverrideHeaterRangeTemperatureSchema,
  planningOverrideHeaterRangeComfortSchema,
  planningOverrideHeaterRangeEcoSchema,
  planningOverrideHeaterRangeNoFrostSchema,
]);

const planningOverrideOnRangeSchema = basePlanningOverrideRangeSchema.extend({
  metaMode: z.nativeEnum(EOnProgramMetaMode),
});

const planningOverrideRangeSchema = z.union([
  planningOverrideHeaterRangeSchema,
  planningOverrideOnRangeSchema,
  planningOverrideOffRangeSchema,
  planningOverrideOccupiedRangeSchema,
  planningOverrideAvailableRangeSchema,
]);

const sectionPlanningSchema = z.object({
  sectionId: z.number(),
  sectionName: z.string(),
  ranges: z.array(planningOverrideRangeSchema),
});

export const planningOverridesBySectionSchema = z.object({
  applianceCategory: z.nativeEnum(EProgramApplianceCategory),
  sections: z.array(sectionPlanningSchema),
});

export const planningMetaModeSchema = z.union([
  z.nativeEnum(EHeaterProgramMetaMode),
  z.nativeEnum(EOnProgramMetaMode),
  z.nativeEnum(ECommonProgramMetaMode),
  z.nativeEnum(EBookableSpaceMetaMode),
]);

export const applyPlanningOverrideRangeSchema = z.object({
  startTime: z.string(), // string instead of date because back is not TZ ready....
  endTime: z.string(), // string instead of date because back is not TZ ready....
  metaMode: planningMetaModeSchema,
  temperature: z.number().nullish(),
});

const sectionRangesSchema = z.object({
  sectionId: z.number(),
  ranges: z.array(applyPlanningOverrideRangeSchema),
});

export const applyPlanningOverrideSchema = z.object({
  sectionRanges: z.array(sectionRangesSchema),
});

export type TApplyPlanningOverrideRange = z.infer<typeof applyPlanningOverrideRangeSchema>;
export type TApplyPlanningOverride = z.infer<typeof applyPlanningOverrideSchema>;
export type TPlanningOverrideHeaterRangeTemperature = z.infer<typeof planningOverrideHeaterRangeTemperatureSchema>;
export type TPlanningOverrideHeaterRange = z.infer<typeof planningOverrideHeaterRangeSchema>;
export type TPlanningOverrideRange = z.infer<typeof planningOverrideRangeSchema>;
export type TPlanningOverrideWaterHeaderRange = z.infer<typeof planningOverrideOnRangeSchema>;
export type TPlanningOverrideBySection = z.infer<typeof planningOverridesBySectionSchema>;
export type TSectionPlanning = z.infer<typeof sectionPlanningSchema>;
